<template>
  <v-navigation-drawer
    class="product-filters-sidebar"
    :elevation="0"
    :width="$vuetify.display.xs ? $vuetify.display.width : 300"
    :rounded="$vuetify.display.xs"
    :permanent="!$vuetify.display.xs && drawerStore.sidebarOpen"
    :disable-resize-watcher="true"
    :temporary="$vuetify.display.xs"
    :location="$vuetify.display.xs ? 'bottom' : 'start'"
    :model-value="drawerStore.sidebarOpen"
    :scrim="true"
    :touchless="true"
  >
    <div v-if="$vuetify.display.xs" class="mobile-filter-title d-flex align-center py-5 px-6">Filtrs</div>
    <v-divider />

    <div v-if="!productFilterStore.valuesloading" class="ma-6">
      <!-- Ebook exclusive filters -->
      <div v-if="showEbookFilters" class="my-4">
        <v-row class="pt-2 ebook-filters">
          <v-col cols="12" class="filter-block-title pa-0">E-grāmatas tips</v-col>
          <v-col v-for="(category, idx) in ebookCategories" :key="idx" cols="12" class="pa-0">
            <v-checkbox
              v-model="productFilterStore.filters.categories"
              :value="category"
              :label="category.title"
              density="compact"
              hide-details
              false-icon="fa-regular fa-square"
              true-icon="fa-solid fa-square-check"
              class="filter-checkbox"
              :class="{ selected: isSelected(category, 'ebook') }"
              :disabled="productFilterStore.loading"
              :ripple="false"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Grades. Available always -->
      <div class="my-4">
        <v-row class="pt-2 grade-filters">
          <v-col cols="12" class="filter-block-title pa-0">Klases</v-col>
          <v-col v-for="(grade, idx) in grades" :key="idx" cols="6" class="pa-0">
            <v-checkbox
              v-model="productFilterStore.filters.grades"
              :value="grade"
              :label="grade.title"
              density="compact"
              hide-details
              false-icon="fa-regular fa-square"
              true-icon="fa-solid fa-square-check"
              class="filter-checkbox"
              :class="{ selected: isSelected(grade, 'grade') }"
              :disabled="productFilterStore.loading"
              :ripple="false"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Subjects. Avaiable always -->
      <div class="my-4">
        <v-row class="pt-2 subject-filters">
          <v-col cols="12" class="filter-block-title pa-0">Mācību priekšmets</v-col>
          <v-col v-for="(subject, idx) in subjects" :key="idx" cols="12" class="pa-0">
            <v-checkbox
              v-model="productFilterStore.filters.subjects"
              :value="subject"
              :label="subject.title"
              density="compact"
              hide-details
              false-icon="fa-regular fa-square"
              true-icon="fa-solid fa-square-check"
              class="filter-checkbox"
              :class="{ selected: isSelected(subject, 'subject') }"
              :disabled="productFilterStore.loading"
              :ripple="false"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Curriculum types. Not available on "video" section -->
      <div v-if="showCurriculumFilters" class="my-4">
        <v-row class="pt-2 curriculum-type-filters">
          <v-col cols="12" class="filter-block-title pa-0">Mācību saturs</v-col>
          <v-col v-for="(type, idx) in curriculumTypes" :key="idx" cols="12" class="pa-0">
            <v-checkbox
              v-model="productFilterStore.filters.curriculumTypes"
              :value="type"
              :label="type.title"
              density="compact"
              hide-details
              false-icon="fa-regular fa-square"
              true-icon="fa-solid fa-square-check"
              class="filter-checkbox"
              :class="{ selected: isSelected(type, 'type') }"
              :disabled="productFilterStore.loading"
              :ripple="false"
            />
          </v-col>
        </v-row>
      </div>

      <!-- Button that resets all filters to empty -->
      <div class="mt-8">
        <v-btn
          rounded="pill"
          variant="tonal"
          class="filter-reset-button w-100"
          :disabled="productFilterStore.loading"
          @click="resetFilters()"
        >
          Nodzēst filtrus
        </v-btn>
      </div>
    </div>

    <div v-else class="w-100 h-100 d-flex justify-center align-center">
      <v-progress-circular indeterminate color="secondary" />
    </div>
  </v-navigation-drawer>
</template>

<script lang="ts">
import { isEbookSection, isVideoSection, isActivInspireSection } from "~/helpers/sidebar"
import storage from "~/services/storage"
import { useDrawerStore } from "~/stores/drawerStore"
import { userProductFilterStore } from "~/stores/productFilterStore"
import { SlugTitleType } from "~/types/schema/index"

export default {
  setup() {
    const drawerStore = useDrawerStore()
    const productFilterStore = userProductFilterStore()

    return {
      drawerStore,
      productFilterStore,
    }
  },

  computed: {
    showEbookFilters() {
      return isEbookSection(this.$route)
    },

    showCurriculumFilters() {
      return !isVideoSection(this.$route) && !isActivInspireSection(this.$route)
    },

    ebookCategories() {
      return this.productFilterStore.filterValues.ebook_categories
    },

    grades() {
      return this.productFilterStore.filterValues.grades
    },

    subjects() {
      return this.productFilterStore.filterValues.subjects
    },

    curriculumTypes() {
      return this.productFilterStore.filterValues.curriculum_types
    },
  },

  watch: {
    "productFilterStore.filters": {
      handler() {
        this.updateLocalStorage()
        this.productFilterStore.fetchProducts({})
      },
      deep: true,
    },
  },

  methods: {
    updateLocalStorage() {
      storage.setProductFilters(this.$route.name as string, this.productFilterStore.filters)
    },

    fetchFilterValues() {
      this.productFilterStore.fetchFilterValues({})
    },

    isSelected(item: SlugTitleType, itemType: string) {
      let searchArray = [] as SlugTitleType[]
      if (itemType === "ebook") {
        searchArray = this.productFilterStore.filters.categories
      } else if (itemType === "grade") {
        searchArray = this.productFilterStore.filters.grades
      } else if (itemType === "subject") {
        searchArray = this.productFilterStore.filters.subjects
      } else {
        searchArray = this.productFilterStore.filters.curriculumTypes
      }

      for (let i = 0; i < searchArray.length; i++) {
        if (searchArray[i].slug === item.slug) {
          return true
        }
      }

      return false
    },

    resetFilters() {
      this.productFilterStore.resetFilters()
    },
  },
}
</script>

<style lang="sass">
@use "~/assets/css/vuetify/settings" as *

@media #{map-get($display-breakpoints, 'sm-and-up')}
  .product-filters-sidebar
    .filter-checkbox
      &:hover
        background: rgba(0, 0, 0, 0.08)
      &.selected
        &:hover
          background: linear-gradient(0deg, rgba(0, 0, 0, 0.08), rgba(0, 0, 0, 0.08)), #FFF1AE

.product-filters-sidebar
  .mobile-filter-title
    height: 68px
    font-weight: 400
    font-size: 20px
    letter-spacing: 0.25px
  .filter-reset-button
    text-transform: none
    min-height: 44px
    color: black
    letter-spacing: 0.1px
    font-size: 16px
    font-weight: 700
    font-style: normal
  .filter-block-title
    font-style: normal
    font-size: 14px
    font-weight: 700
    letter-spacing: 0.1px
    color: black
    margin-bottom: 12px
  .filter-checkbox
    margin-top: 1px
    margin-bottom: 1px
    border-radius: 8px
    margin-left: 4px
    margin-right: 4px
    padding: 0px 8px
    .v-input__control
      .v-selection-control__input
        &::before
          background: none
      .v-label
        font-style: normal
        font-weight: 400
        font-size: 14px
        color: black
        opacity: 1
        letter-spacing: 0.1px
        padding-left: 6px
      i
        opacity: 1
        font-size: 16px
    &.selected
      background: #FFF1AE
      .v-input__control
        .v-label
          font-weight: 700
</style>
